import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MagnifyingGlass, Plus, Robot, Warning } from 'phosphor-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cadastrarBot, gerarToken, listarBots, obterResumoMigracao } from '../api';
import { IDENTIDADE_META, STATUS_META, TIPOS_BOT, TIPO_META } from '../constants';
import { CadastrarBotModal } from '../components/CadastrarBotModal';
import { TokenGeradoModal } from '../components/TokenGeradoModal';
import { BotDetalheDrawer } from '../components/BotDetalheDrawer';
import type { IBot, ICadastrarBot, ITokenGerado, StatusBot, TipoBot } from '../types';

const formatarData = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString('pt-BR') : '—';

export const BotsTab: React.FC = () => {
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<TipoBot | ''>('');
  const [filtroStatus, setFiltroStatus] = useState<StatusBot | ''>('');
  const [botAberto, setBotAberto] = useState<string | null>(null);
  const [tokenGerado, setTokenGerado] = useState<ITokenGerado | null>(null);

  const cadastroModal = useDisclosure();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: bots = [], isLoading } = useQuery({
    queryKey: ['automacao', 'bots', { tipo: filtroTipo, status: filtroStatus }],
    queryFn: () =>
      listarBots({
        tipo_bot: filtroTipo || undefined,
        status: filtroStatus || undefined,
      }),
    refetchInterval: 60000,
  });

  const { data: migracao } = useQuery({
    queryKey: ['automacao', 'migracao'],
    queryFn: obterResumoMigracao,
    refetchInterval: 60000,
  });

  const criar = useMutation({
    mutationFn: cadastrarBot,
    onSuccess: (bot: IBot) => {
      queryClient.invalidateQueries({ queryKey: ['automacao'] });
      cadastroModal.onClose();
      toast({
        title: `${bot.bot_name} cadastrado`,
        description: 'Gere um token para ele conseguir falar com a API.',
        status: 'success',
      });
      setBotAberto(bot.bot_name);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast({ title: 'Não deu para cadastrar', description: msg, status: 'error' });
    },
  });

  const gerar = useMutation({
    mutationFn: ({ tipo, nome }: { tipo: TipoBot; nome: string }) => gerarToken(tipo, nome),
    onSuccess: token => {
      queryClient.invalidateQueries({ queryKey: ['automacao'] });
      setTokenGerado(token);
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast({ title: 'Não deu para gerar o token', description: msg, status: 'error' });
    },
  });

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return bots;
    return bots.filter(
      b =>
        b.bot_name.toLowerCase().includes(termo) ||
        (b.apelido || '').toLowerCase().includes(termo) ||
        (b.area || '').toLowerCase().includes(termo),
    );
  }, [bots, busca]);

  const resumo = useMemo(() => {
    const conta = (s: StatusBot) => bots.filter(b => b.status === s).length;
    return {
      total: bots.length,
      ativos: conta('ativo'),
      pendentes: conta('nao_provisionado') + conta('aguardando_handshake'),
    };
  }, [bots]);

  const pedirToken = (botName: string) => {
    const bot = bots.find(b => b.bot_name === botName);
    if (bot) gerar.mutate({ tipo: bot.tipo_bot, nome: bot.bot_name });
  };

  const renderLista = () => {
    if (isLoading) {
      return (
        <Flex justify="center" py={12}>
          <Spinner />
        </Flex>
      );
    }

    if (filtrados.length === 0) {
      return (
        <VStack py={12} spacing={3} color="gray.500">
          <Robot size={40} />
          <Text>
            {bots.length === 0 ? 'Nenhum bot cadastrado ainda.' : 'Nada com esse filtro.'}
          </Text>
          {bots.length === 0 && (
            <Button size="sm" colorScheme="blue" onClick={cadastroModal.onOpen}>
              Cadastrar o primeiro
            </Button>
          )}
        </VStack>
      );
    }

    return (
      <Box borderWidth="1px" borderRadius="lg" overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>Bot</Th>
              <Th>Tipo</Th>
              <Th>Área</Th>
              <Th>Status</Th>
              <Th>Identidade</Th>
              <Th>Credenciais</Th>
              <Th>Último uso</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtrados.map(bot => {
              const meta = STATUS_META[bot.status];
              return (
                <Tr
                  key={bot.bot_name}
                  _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                  onClick={() => setBotAberto(bot.bot_name)}
                >
                  <Td>
                    <Text fontWeight="medium">{bot.apelido || bot.bot_name}</Text>
                    {bot.apelido && (
                      <Code fontSize="10px" color="gray.500">
                        {bot.bot_name}
                      </Code>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={TIPO_META[bot.tipo_bot].cor}>
                      {TIPO_META[bot.tipo_bot].label}
                    </Badge>
                  </Td>
                  <Td fontSize="xs" color="gray.600">
                    {bot.area || '—'}
                  </Td>
                  <Td>
                    <Badge colorScheme={meta.cor}>{meta.label}</Badge>
                  </Td>
                  <Td>
                    {bot.identidade && (
                      <Tooltip label={IDENTIDADE_META[bot.identidade].explicacao} hasArrow>
                        <Badge colorScheme={IDENTIDADE_META[bot.identidade].cor} variant="subtle">
                          {IDENTIDADE_META[bot.identidade].label}
                        </Badge>
                      </Tooltip>
                    )}
                  </Td>
                  <Td fontSize="xs">
                    {bot.tokens_ativos ? (
                      `${bot.tokens_ativos} ativa${bot.tokens_ativos > 1 ? 's' : ''}`
                    ) : (
                      <Text color="orange.500">nenhuma</Text>
                    )}
                  </Td>
                  <Td fontSize="xs" color="gray.600">
                    {formatarData(bot.ultimo_uso_em)}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  return (
    <VStack align="stretch" spacing={5}>
      {/* Enquanto houver bot no token compartilhado, ele não pode ser desligado — e
          quem tiver esse token grava log em nome desses bots. É a pendência que
          precisa ficar visível até virar zero. */}
      {migracao && migracao.faltam_migrar > 0 && (
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon as={Warning} />
          <Box fontSize="sm">
            <Text fontWeight="semibold">
              {migracao.faltam_migrar} bot{migracao.faltam_migrar > 1 ? 's' : ''} ainda usa
              {migracao.faltam_migrar > 1 ? 'm' : ''} o token compartilhado
            </Text>
            <Text color="gray.600">
              Enquanto isso, qualquer processo com esse token pode gravar log em nome deles — e ele
              não pode ser desligado. {(migracao.bots.legado || []).slice(0, 6).join(', ')}
              {(migracao.bots.legado || []).length > 6 && ' e outros'}
            </Text>
          </Box>
        </Alert>
      )}

      <HStack spacing={6}>
        <Stat size="sm">
          <StatLabel color="gray.500">Bots</StatLabel>
          <StatNumber fontSize="2xl">{resumo.total}</StatNumber>
        </Stat>
        <Stat size="sm">
          <StatLabel color="gray.500">Ativos</StatLabel>
          <StatNumber fontSize="2xl" color="green.500">
            {resumo.ativos}
          </StatNumber>
        </Stat>
        <Stat size="sm">
          <StatLabel color="gray.500">Aguardando provisionamento</StatLabel>
          <StatNumber fontSize="2xl" color={resumo.pendentes ? 'orange.500' : 'gray.400'}>
            {resumo.pendentes}
          </StatNumber>
        </Stat>
      </HStack>

      <Flex gap={3} wrap="wrap" align="center">
        <InputGroup maxW="320px" size="sm">
          <InputLeftElement pointerEvents="none">
            <MagnifyingGlass size={16} />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nome, apelido ou área"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>

        <Select
          size="sm"
          maxW="150px"
          value={filtroTipo}
          onChange={e => setFiltroTipo(e.target.value as TipoBot | '')}
          borderRadius="md"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_BOT.map(t => (
            <option key={t} value={t}>
              {TIPO_META[t].label}
            </option>
          ))}
        </Select>

        <Select
          size="sm"
          maxW="210px"
          value={filtroStatus}
          onChange={e => setFiltroStatus(e.target.value as StatusBot | '')}
          borderRadius="md"
        >
          <option value="">Todos os status</option>
          {(Object.keys(STATUS_META) as StatusBot[]).map(s => (
            <option key={s} value={s}>
              {STATUS_META[s].label}
            </option>
          ))}
        </Select>

        <Box flex={1} />

        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<Plus size={16} />}
          onClick={cadastroModal.onOpen}
        >
          Cadastrar bot
        </Button>
      </Flex>

      {renderLista()}

      <CadastrarBotModal
        isOpen={cadastroModal.isOpen}
        onClose={cadastroModal.onClose}
        onSubmit={(payload: ICadastrarBot) => criar.mutate(payload)}
        isSaving={criar.isPending}
      />

      <BotDetalheDrawer
        botName={botAberto}
        onClose={() => setBotAberto(null)}
        onGerarToken={pedirToken}
      />

      <TokenGeradoModal token={tokenGerado} onClose={() => setTokenGerado(null)} />
    </VStack>
  );
};
